import img_sleep_debt_high from './assets/sleep-debt-high.webp';
import img_sleep_debt_low from './assets/sleep-debt-low.webp';
import img_sleep_debt_moderate from './assets/sleep-debt-moderate.webp';
import {growthBook} from 'utils/growthbook';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {INSURANCE_PROVIDERS, INSURANCE_STATES} from './config';
import {getPayerIdByValue} from '../paywall/utils';
import {isSleepioAllowedForStateAndPayer} from 'sleepioRx/helpers';
import {SleepioAllowedPayersByState} from 'sleepioRx/types';
import Analytics from 'utils/Analytics';

const trackInsuranceSubmitted = ({
  state,
  insurance,
  payerId,
  sleepioIsAvailable,
  insuranceIsValid,
}: {
  state: unknown;
  insurance: unknown;
  payerId?: string;
  sleepioIsAvailable?: boolean;
  insuranceIsValid?: boolean;
}) => {
  if (!state || !insurance) {
    return;
  }

  Analytics.track('Clinic Web Intake Insurance Submitted', {
    insurance_state: typeof state === 'string' ? state.toUpperCase() : state,
    insurance_coverage: insurance,
    insurance_payer_id: payerId,
    sleepio_is_available: sleepioIsAvailable,
    insurance_is_valid: insuranceIsValid,
    // Guarded on the current pick: a patient who chose Medicare, then went back
    // to another payer, still has the stale answer in survey data.
    medicare_plan_type: insurance === 'medicare' ? surveyInstance.getValue('medicare_plan_type') : undefined,
  });
};

/**
 * SleepioRx eligibility check for the `/start` funnel, where clinic appointment
 * booking is paused. Always sets `insurance_is_valid: 'no'` (booking is off this
 * path) and derives `sleepio_is_available` from the `sleepio_allowed_payers_web`
 * state/payer allowlist.
 *
 * Must run BEFORE the validation interstitial so SurveyJS knows which pages are
 * visible when it decides whether it's on the last page.
 */
export const validateSleepioEligibility = () => {
  const allowedPayers = growthBook.getFeatureValue('sleepio_allowed_payers_web', {}) as SleepioAllowedPayersByState;
  const state = surveyInstance.getValue('insurance_state');
  const normalizedState = typeof state === 'string' ? state.toUpperCase() : state;
  const insurance = surveyInstance.getValue('insurance_coverage');
  const sleepioIsAvailable = isSleepioAllowedForStateAndPayer(allowedPayers, state, insurance);
  const payerId = getPayerIdByValue(insurance);
  trackInsuranceSubmitted({state, insurance, payerId, sleepioIsAvailable, insuranceIsValid: false});

  surveyInstance.data = {
    ...surveyInstance.data,
    insurance_is_valid: 'no',
    clinic_booking_paused: true,
    sleepio_is_available: sleepioIsAvailable ? 'yes' : 'no',
    sleepio_not_available_reason:
      !normalizedState || !allowedPayers[normalizedState] ? 'state_ineligible' : 'payer_ineligible',
    sleepio_payer_id: payerId,
  };
  SurveyData.save(surveyInstance);
};

/**
 * Clinic appointment-booking eligibility for the `/book-appointment` funnel.
 * Sets `insurance_is_valid` from the `cbti_allowed_providers_web` state → payerId
 * allowlist, which gates the member-ID, time-selector, and contact-info pages.
 *
 * `sleepio_is_available` is pinned to 'no' so the SleepioRx branch can never open
 * on this path, and `clinic_booking_paused` to false so the waitlist/app-handoff
 * analytics distinguish the two funnels.
 *
 * Must run BEFORE the validation interstitial — see validateSleepioEligibility.
 */
export const validateBookingEligibility = () => {
  const allowedProviders = growthBook.getFeatureValue('cbti_allowed_providers_web', {}) as Record<string, string[]>;
  const state = surveyInstance.getValue('insurance_state');
  const normalizedState = typeof state === 'string' ? state.toUpperCase() : state;
  const insurance = surveyInstance.getValue('insurance_coverage');

  if (!state || !insurance) {
    surveyInstance.data = {
      ...surveyInstance.data,
      insurance_is_valid: 'no',
      clinic_booking_paused: false,
      sleepio_is_available: 'no',
    };
    return;
  }

  const payerId = getPayerIdByValue(insurance);
  const providersForState = allowedProviders[normalizedState];
  const valid = Array.isArray(providersForState) && providersForState.includes(payerId as string);
  trackInsuranceSubmitted({state, insurance, payerId, insuranceIsValid: valid});

  surveyInstance.data = {
    ...surveyInstance.data,
    insurance_is_valid: valid ? 'yes' : 'no',
    clinic_booking_paused: false,
    sleepio_is_available: 'no',
  };
  SurveyData.save(surveyInstance);
};

export const getSleepDebtColor = (sleepDebtZone: string) => {
  switch (sleepDebtZone) {
    case 'high':
      return '#E51542';
    case 'low':
      return '#008568';
    case 'moderate':
    default:
      return '#B85C00';
  }
};

export const getSleepDebtChartImage = (sleepDebtZone: string) => {
  switch (sleepDebtZone) {
    case 'high':
      return img_sleep_debt_high;
    case 'low':
      return img_sleep_debt_low;
    case 'moderate':
    default:
      return img_sleep_debt_moderate;
  }
};

/**
 * Sanitizes name input to only allow valid characters
 * Allows: letters (any language), spaces, apostrophes (including smart quotes), periods, and hyphens
 * Smart quotes (\u2018 ' and \u2019 ') are included because iOS keyboards auto-insert them
 */
export const sanitizeName = (name: string): string => {
  // Allow: letters (any language), spaces, apostrophes (including smart quotes), periods, and hyphens
  // Smart quotes (\u2018 ' and \u2019 ') are included because iOS keyboards auto-insert them
  // This regex keeps only valid name characters and removes everything else
  // Note: Hyphen is placed at the end of the character class to avoid range interpretation
  return name.replace(/[^a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\s'.'\u2018\u2019-]/g, '');
};

/**
 * Input mask that sanitizes name input in real-time
 * To be used with SurveyJS text input fields
 */
export const nameMask = (inputElement: HTMLInputElement, question?: any) => {
  const handleInput = () => {
    const cursorPosition = inputElement.selectionStart;
    const originalLength = inputElement.value.length;
    const sanitized = sanitizeName(inputElement.value);

    if (sanitized !== inputElement.value) {
      inputElement.value = sanitized;
      // Adjust cursor position if characters were removed
      const lengthDiff = originalLength - sanitized.length;
      const newPosition = Math.max(0, (cursorPosition ?? 0) - lengthDiff);
      inputElement.setSelectionRange(newPosition, newPosition);

      // Trigger input event so SurveyJS updates its internal state
      inputElement.dispatchEvent(new Event('input', {bubbles: true}));
    }

    if (question) {
      question.value = sanitized;
    }
  };

  inputElement.addEventListener('input', handleInput);
};

export function getInsuranceOptionsForState(state?: string): Array<{text: string; value: string}> {
  // Safely handle state conversion
  const stateCode = state?.toUpperCase();

  console.log('stateCode', stateCode);

  // Find providers for state or fall back to default
  const stateProviders =
    INSURANCE_STATES.find(s => s.state === stateCode)?.providers ??
    INSURANCE_STATES.find(s => s.state === 'Default')?.providers ??
    [];

  // Map to options array with type safety
  return stateProviders
    .map(value => {
      const insuranceProvider = INSURANCE_PROVIDERS.find(p => p.value === value);
      return insuranceProvider?.label ? {text: insuranceProvider.label, value: insuranceProvider.value} : null;
    })
    .filter((option): option is {text: string; value: string} => option !== null);
}
